/* DVY long-form PDP sections: sale countdown, carousel, video cards, offer links. Vanilla web components. */

/**
 * Parses an end date. With an explicit offset ("Z" or "+hh:mm") it is used as-is; otherwise the
 * wall-clock time is read in the given IANA time zone (e.g. America/Los_Angeles).
 * @returns {number} epoch ms, or NaN
 */
function dvyParseEnd(value, timeZone) {
  const text = String(value || '').trim();
  if (!text) return NaN;
  if (/[zZ]$|[+-]\d\d:?\d\d$/.test(text)) return Date.parse(text);
  const m = /^(\d{4})-(\d\d)-(\d\d)(?:[T ](\d\d):(\d\d)(?::(\d\d))?)?$/.exec(text);
  if (!m) return NaN;
  // A date without a time means the end of that day.
  const [h, min, sec] = m[4] ? [+m[4], +m[5], +(m[6] || 0)] : [23, 59, 59];
  const wall = Date.UTC(+m[1], +m[2] - 1, +m[3], h, min, sec);
  // Offset of the zone at that moment: format the instant in the zone and compare.
  const zoned = (ms) => {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-US', { timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })
        .formatToParts(new Date(ms))
        .map((p) => [p.type, p.value])
    );
    return Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
  };
  const guess = wall - (zoned(wall) - wall);
  return wall - (zoned(guess) - guess);
}

/** Sale bar: counts down to one fixed end date for every visitor; removes itself once it has passed. */
class DvyCountdown extends HTMLElement {
  connectedCallback() {
    this.end = dvyParseEnd(this.dataset.end, this.dataset.timeZone || 'America/Los_Angeles');
    this.out = this.querySelector('[data-dvy-countdown-value]');
    this.bar = this.closest('[data-dvy-sale-bar]') || this;
    if (!Number.isFinite(this.end)) return this.expire();
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  expire() {
    clearInterval(this.timer);
    this.bar.hidden = true;
  }

  tick() {
    const left = Math.floor((this.end - Date.now()) / 1000);
    if (left <= 0) return this.expire();
    const pad = (n) => String(n).padStart(2, '0');
    const d = Math.floor(left / 86400);
    const h = Math.floor((left % 86400) / 3600);
    const m = Math.floor((left % 3600) / 60);
    const s = left % 60;
    this.out.textContent = left > 86400 ? `${d}d ${pad(h)}h ${pad(m)}m` : `${pad(h)}h ${pad(m)}m ${pad(s)}s`;
    this.bar.hidden = false;
  }
}

/** Horizontal scroll-snap carousel: swipe on touch, arrow buttons on desktop. */
class DvyCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-dvy-carousel-track]');
    this.prev = this.querySelector('[data-dvy-carousel-prev]');
    this.next = this.querySelector('[data-dvy-carousel-next]');
    if (!this.track) return;
    const step = () => {
      const card = this.track.firstElementChild;
      return card ? card.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || 0) : this.track.clientWidth;
    };
    this.prev?.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: 'smooth' }));
    this.next?.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: 'smooth' }));
    const sync = () => {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max;
    };
    this.track.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();
  }
}

/** Testimonial video: loads on approach, plays muted while in view, tap toggles sound. Never autoplays with sound. */
class DvyVideoCard extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.button = this.querySelector('[data-dvy-video-sound]');
    if (!this.video) return;
    this.video.muted = true;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.load();
          if (!reduced) this.video.play().catch(() => {});
        } else {
          this.video.pause();
          this.setMuted(true);
        }
      },
      { threshold: 0.6 }
    );
    this.observer.observe(this);

    this.button?.addEventListener('click', () => {
      this.load();
      this.setMuted(!this.video.muted);
      this.video.play().catch(() => {});
    });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  load() {
    if (this.loaded) return;
    this.loaded = true;
    this.video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
    });
    this.video.load();
  }

  setMuted(muted) {
    this.video.muted = muted;
    this.button?.setAttribute('aria-pressed', String(!muted));
    const label = muted ? this.button?.dataset.labelOn : this.button?.dataset.labelOff;
    if (label) this.button.setAttribute('aria-label', label);
  }
}

/** Every "Start my ritual" link scrolls to the page's offer block (selection kept) instead of the cart. */
document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-dvy-to-offer]');
  if (!link) return;
  const offer = document.querySelector('dvy-offer-selector');
  if (!offer) return;
  event.preventDefault();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  offer.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  offer.querySelector('input[name="id"]:checked')?.focus({ preventScroll: true });
});

if (!customElements.get('dvy-countdown')) customElements.define('dvy-countdown', DvyCountdown);
if (!customElements.get('dvy-carousel')) customElements.define('dvy-carousel', DvyCarousel);
if (!customElements.get('dvy-video-card')) customElements.define('dvy-video-card', DvyVideoCard);
